import { z } from 'zod'

/**
 * Public (`NEXT_PUBLIC_*`) environment, validated with Zod.
 *
 * These are inlined into the client bundle at build, so each var must be
 * referenced as a literal `process.env.NEXT_PUBLIC_*` below — a dynamic lookup
 * would not be replaced. `.catch()` keeps a malformed public env from
 * white-screening the browser; it falls back to safe defaults instead.
 */

const DEFAULT_APP_URL = 'https://www.dourso.com.br'
const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com'

function blankToUndefined(v: unknown) {
  return typeof v === 'string' && v.trim() === '' ? undefined : v
}

function envField<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess(blankToUndefined, schema)
}

const clientEnvSchema = z.object({
  NEXT_PUBLIC_APP_URL: envField(z.string().url().default(DEFAULT_APP_URL)),
  NEXT_PUBLIC_API_URL: envField(z.string().url().optional()),
  // Caught per field: the object-level `.catch()` below would otherwise let a
  // mistyped analytics key discard every other public value (Stripe key
  // included). Analytics turns off; the server env still fails loudly on it.
  NEXT_PUBLIC_POSTHOG_KEY: envField(z.string().startsWith('phc_').optional()).catch(undefined),
  NEXT_PUBLIC_POSTHOG_HOST: envField(z.string().url().default(DEFAULT_POSTHOG_HOST)).catch(DEFAULT_POSTHOG_HOST),
  NEXT_PUBLIC_MP_PUBLIC_KEY: envField(z.string().min(1).optional()),
  NEXT_PUBLIC_USE_MOCK_PAYMENTS: envField(z.string().min(1).optional()),
})

export type ClientEnv = z.infer<typeof clientEnvSchema>

export const clientEnv: ClientEnv = clientEnvSchema
  .catch({ NEXT_PUBLIC_APP_URL: DEFAULT_APP_URL, NEXT_PUBLIC_POSTHOG_HOST: DEFAULT_POSTHOG_HOST })
  .parse({
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_MP_PUBLIC_KEY: process.env.NEXT_PUBLIC_MP_PUBLIC_KEY,
    NEXT_PUBLIC_USE_MOCK_PAYMENTS: process.env.NEXT_PUBLIC_USE_MOCK_PAYMENTS,
  })

const TRAILING_SLASH_RE = /\/$/

export function appUrl(): string {
  return clientEnv.NEXT_PUBLIC_APP_URL.replace(TRAILING_SLASH_RE, '')
}

export function isMockPaymentsEnabled(): boolean {
  return clientEnv.NEXT_PUBLIC_USE_MOCK_PAYMENTS === '1'
    || clientEnv.NEXT_PUBLIC_USE_MOCK_PAYMENTS === 'true'
}
